import { Box, Heading, Text, List, ListItem, Link, useStyleConfig, Button } from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const sectionStyles = useStyleConfig("Section");
  const highlightBoxStyles = useStyleConfig("HighlightBox");
  const navigate = useNavigate();

  return (
    <Box height="100vh" overflowY="auto" bg="gray.50" p={{ base: 4, md: 8 }}>
      <Box maxW="4xl" mx="auto" bg="white" borderRadius="lg" boxShadow="sm" p={{ base: 6, md: 8 }}>
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="link"
          color="blue.600"
          mb={4}
          onClick={() => navigate('/')} // or '/login' based on your route
        >
          Back to Login
        </Button>
        <Box borderBottom="1px" borderColor="gray.200" pb={6} mb={8}>
          <Heading as="h1" size="xl" mb={4} color="gray.800">
            Privacy Policy
          </Heading>
          <Box color="gray.600" spacing={2}>
            <Text>Effective Date: April 20, 2025</Text>
            <Text>Last Updated: April 20, 2025</Text>
          </Box>
        </Box>

        <Box sx={{ 'p': { mb: 6 }, 'ul': { mb: 4 } }}>
          <Text mb={6}>
            Creatives Matter LLC ("we", "our", or "us") values your privacy. This Privacy Policy explains
            how we collect, use, store, and disclose information when you use our web application ("Web App"),
            which allows users to add sales data, track sales progress, manage customer information, send
            invoices via their email accounts, and send sales emails to prospective customers.
          </Text>

          <Text fontWeight="semibold" mb={8}>
            By using our Web App, you agree to the terms of this Privacy Policy.
          </Text>

          <Section title="1. Information We Collect">
            <SubSection title="a. User-Provided Information">
              <List styleType="disc" pl={6} spacing={2}>
                <ListItem><Text as="strong">Account Information:</Text> Name, email address, and other contact details.</ListItem>
                <ListItem><Text as="strong">Sales Data:</Text> Information about your sales activity, deals, stages, and outcomes.</ListItem>
                <ListItem><Text as="strong">Customer Information:</Text> Contact and profile details for your customers or leads.</ListItem>
                <ListItem><Text as="strong">Email Access Authorization:</Text> OAuth or other access credentials to send emails on your behalf.</ListItem>
              </List>
            </SubSection>

            <SubSection title="b. Automatically Collected Information">
              <Text>IP address, browser type, operating system, referring URLs, usage data, and cookies.</Text>
            </SubSection>
          </Section>

          <Section title="2. How We Use Your Information">
            <Text>We use the collected information to:</Text>
            <List styleType="disc" pl={6} spacing={2} mt={2}>
              <ListItem>Provide, maintain, and improve the Web App</ListItem>
              <ListItem>Facilitate the tracking of sales progress</ListItem>
              <ListItem>Enable you to send invoices and sales emails through your email account</ListItem>
              <ListItem>Provide customer support</ListItem>
              <ListItem>Communicate updates, promotions, or other service-related messages (you can opt out anytime)</ListItem>
              <ListItem>Use your uploaded customer information for our own marketing and sales purposes, including identifying trends, targeting potential markets, and developing promotional strategies</ListItem>
            </List>
          </Section>

          <Section title="3. Email Access and Usage">
            <Box {...highlightBoxStyles}>
              <Text>With your permission, we access your email account solely to:</Text>
              <List styleType="disc" pl={6} mt={2}>
                <ListItem>Send invoices to your customers</ListItem>
                <ListItem>Send sales emails to prospective leads</ListItem>
              </List>
              <Text mt={4}>
                We do not read, store, or analyze your emails beyond what is required to complete these actions.
                Your email credentials are never stored on our servers.
              </Text>
            </Box>
          </Section>

          <Section title="4. Data Sharing and Disclosure">
            <Box {...highlightBoxStyles}>
              <Text>We do not sell or rent your personal data to third parties. We may share your information only in the following circumstances:</Text>
              <List styleType="disc" pl={6} mt={2}>
                <ListItem>With service providers: Only to the extent necessary to provide functionality.</ListItem>
                <ListItem>Legal requirements: When required to comply with laws or lawful requests.</ListItem>
                <ListItem>Business transfers: If we are involved in a merger, acquisition, or asset sale.</ListItem>
              </List>
            </Box>
          </Section>

          <Section title="5. Data Security">
              <Text>We use industry-standard encryption, access controls, and secure servers to protect your data. However, no method of transmission over the internet is 100% secure.</Text>
          </Section>

          <Section title="6. Data Retention">
              <Text>We retain your data as long as your account is active or as needed to provide you with services. You may request deletion of your data, except customer information, at any time.</Text>
          </Section>

          <Section title="7. Your Rights">
            <Box {...highlightBoxStyles}>
              <Text>Depending on your location, you may have rights under data protection laws, including:</Text>
              <List styleType="disc" pl={6} mt={2}>
                <ListItem>Accessing the data we hold about you</ListItem>
                <ListItem>Requesting correction or deletion</ListItem>
                <ListItem>Objecting to processing or withdrawing consent</ListItem>
              </List>
              <Text mt={4}>
                To exercise any of these rights, contact us at: info@creativesmatter.com
              </Text>
            </Box>
          </Section>

          <Section title="8. Third-Party Links">
              <Text>Our Web App may contain links to third-party websites. We are not responsible for the privacy practices of those sites.</Text>
          </Section>

          <Section title="9. Children’s Privacy">
              <Text>Our Web App is not intended for individuals under 18. We do not knowingly collect personal data from children.</Text>
          </Section>

          <Section title="10. Changes to This Policy">
              <Text>We may update this Privacy Policy. We will notify you of significant changes by posting the new policy on our website or via email.</Text>
          </Section>

          <Section title="11. Google User Data">
            <SubSection title="a. Sharing, Transfer, or Disclosure of Google User Data">
              <Text>We do not share, sell, or rent Google user data with third parties except as required to operate the Service or comply with legal obligations. Data may be shared with trusted service providers who help us operate our application (e.g., email APIs), and only under strict confidentiality agreements. Google user data will never be transferred or used for unauthorized advertising or marketing purposes.</Text>
              </SubSection>
              <SubSection title="b. Data Protection Mechanisms">
              <Text>We implement robust security measures to protect sensitive Google user data, including OAuth 2.0 authentication, encrypted data transmission (TLS), and access controls. Sensitive data is only accessible to authorized systems or personnel and is never stored in plain text.</Text>
              </SubSection>
              <SubSection title="c. Retention and Deletion of Google User Data">
              <Text>We retain Google user data only for as long as necessary to provide the features of the Service you have authorized. You may revoke access at any time through the “Connect” tab in your profile. Upon revocation or account deletion, associated Google user data is deleted from our systems within 30 days unless a longer retention period is required by law.</Text>
              </SubSection>
          </Section>

          <Section title="12. Contact Us">
            <Box bg="gray.50" p={6} borderRadius="lg">
              <Text fontWeight="semibold" mb={2}>
                For any questions about this Privacy Policy, contact us at:
              </Text>
              <Text mb={1}>Creatives Matter LLC</Text>
              <Link href="mailto:info@creativesmatter.com" color="blue.600" _hover={{ textDecoration: 'underline' }}>
                info@creativesmatter.com
              </Link>
            </Box>
          </Section>
        </Box>
      </Box>
    </Box>
  );
};

const Section = ({ title, children }) => (
  <Box mb={8}>
    <Heading as="h2" size="md" mb={4} color="gray.800">
      {title}
    </Heading>
    {children}
  </Box>
);

const SubSection = ({ title, children }) => (
  <Box mb={4} ml={4}>
    <Text fontWeight="medium" mb={2} color="gray.800">
      {title}
    </Text>
    {children}
  </Box>
);

// Custom style configurations
const theme = {
  components: {
    Section: {
      baseStyle: {
        mb: 8
      }
    },
    HighlightBox: {
      baseStyle: {
        bg: 'blue.50',
        p: 4,
        borderRadius: 'md'
      }
    }
  }
};

export default PrivacyPolicy;
